/* eslint-disable prettier/prettier */
// generate-data.ts - Functions to generate all necessary data for the appointment system

// Dynamic conversation modal props
export interface HVADynamicConversationModalProps {
  show: boolean;
  onHide: () => void;
  title: string;
  agentCode: string;
  agentName: string;
  agentImage?: string;
  onError?: (error: Error) => void;
  customer?: string;
  trn?: string;
  dateOfBirth?: Date;
  debtamount?: number;
  currency?: string;
  origin?: string;
  IBAN?: string;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
  maxDiscount: number;
}

export interface FeedbackMessage {
  id: string;
  timestamp: Date;
  type: 'info' | 'validation' | 'transfer' | 'close';
  message: string;
  details?: string;
  success?: boolean;
}

// Parameters for findAvailableSlots
export interface AvailableSlotsParams {
  serviceType: 'examination' | 'doctor';
  serviceId: string;
  date?: string;
  locationId?: string;
}

// Parameters for findAlternatives
export interface AlternativesParams {
  preferredDate: string;
  preferredTime: string;
  preferredLocationId: string;
  serviceType: 'examination' | 'doctor';
  serviceId: string;
}

// Location interface
export interface Location {
  id: string;
  name: string;
  type: 'hospital' | 'diagnostic';
  address: string;
  area: string;
  coordinates: { lat: number; lng: number };
  services: { examinations: boolean; specialists: boolean };
}

// Examination interface
export interface Examination {
  id: string;
  name: {
    es: string;
    en: string;
  };
  code: string;
  duration: number;
  preparationNeeded: boolean;
  preparationInstructions?: {
    es: string;
    en: string;
  };
}

// Specialty interface
export interface Specialty {
  id: string;
  name: {
    es: string;
    en: string;
  };
}

// Doctor interface
export interface Doctor {
  id: string;
  firstName: { es: string; en: string };
  lastName: { es: string; en: string };
  specialtyId: string;
  locationIds: string[];
  languages: string[];
  appointments: { duration: number; daysAvailable: number[] };
}

// Time slot interface
export interface TimeSlot {
  id: string;
  date: string;
  startTime: string;
  locationId: string;
  serviceType: 'examination' | 'doctor';
  serviceId: string;
  booked: boolean;
}

// Generated data interface
export interface HealthCareData {
  locations: Location[];
  examinations: Examination[];
  specialties: Specialty[];
  doctors: Doctor[];
  slots: TimeSlot[];
}

/**
 * Generate all healthcare system data
 * @returns HealthCareData object containing locations, examinations, specialties, doctors, and slots
 */
export function generateHealthCareData(): HealthCareData {
  // const today = new Date();
  
  // 1. Generate locations
  const locations: Location[] = [
    {
      id: "hospital-1",
      name: "Hospital Universitario La Paz",
      type: "hospital",
      address: "Paseo de la Castellana, 261, 28046 Madrid",
      area: "Madrid Norte",
      coordinates: { lat: 40.4806, lng: -3.6865 },
      services: { examinations: true, specialists: true }
    },
    {
      id: "hospital-2",
      name: "Hospital Clínico San Carlos",
      type: "hospital",
      address: "Calle del Prof Martín Lagos, s/n, 28040 Madrid",
      area: "Madrid Centro",
      coordinates: { lat: 40.4414, lng: -3.7173 },
      services: { examinations: true, specialists: true }
    },
    {
      id: "hospital-3",
      name: "Hospital Universitario 12 de Octubre",
      type: "hospital",
      address: "Av. de Córdoba, s/n, 28041 Madrid",
      area: "Madrid Sur",
      coordinates: { lat: 40.3758, lng: -3.7033 },
      services: { examinations: true, specialists: true }
    },
    {
      id: "diagnostic-1",
      name: "Centro Médico – Chamartín",
      type: "diagnostic",
      address: "Calle Agustín de Foxá, 31, 28036 Madrid",
      area: "Chamartín",
      coordinates: { lat: 40.4721, lng: -3.6819 },
      services: { examinations: true, specialists: true }
    },
    {
      id: "diagnostic-2",
      name: "Centro Médico – Salamanca",
      type: "diagnostic",
      address: "Calle Príncipe de Vergara, 53, 28006 Madrid",
      area: "Salamanca",
      coordinates: { lat: 40.4304, lng: -3.6773 },
      services: { examinations: true, specialists: true }
    },
    {
      id: "diagnostic-3",
      name: "Centro Médico – Moncloa",
      type: "diagnostic",
      address: "Calle de la Princesa, 30, 28008 Madrid",
      area: "Moncloa",
      coordinates: { lat: 40.4294, lng: -3.7142 },
      services: { examinations: true, specialists: true }
    },
    {
      id: "diagnostic-4",
      name: "Centro Médico – Atocha",
      type: "diagnostic",
      address: "Paseo de la Infanta Isabel, 17, 28014 Madrid",
      area: "Atocha",
      coordinates: { lat: 40.4066, lng: -3.6896 },
      services: { examinations: true, specialists: true }
    }
  ];

  // 2. Generate examination types
  const examinations: Examination[] = [
    {
      id: "mag-tomography",
      name: {
        es: "Resonancia Magnética",
        en: "Magnetic Resonance Imaging"
      },
      code: "MRI",
      duration: 45,
      preparationNeeded: true,
      preparationInstructions: {
        es: "No coma ni beba durante 4 horas antes de la prueba",
        en: "Do not eat or drink for 4 hours before the examination"
      }
    },
    {
      id: "comp-tomography",
      name: {
        es: "Tomografía Computarizada",
        en: "Computer Tomography"
      },
      code: "CT",
      duration: 30,
      preparationNeeded: true,
      preparationInstructions: {
        es: "No coma durante 6 horas antes de la prueba",
        en: "Do not eat for 6 hours before the examination"
      }
    },
    {
      id: "ultra-abdomen",
      name: {
        es: "Ecografía abdominal completa",
        en: "Complete abdominal ultrasound"
      },
      code: "US-ABD",
      duration: 40,
      preparationNeeded: true,
      preparationInstructions: {
        es: "Acuda con la vejiga llena",
        en: "Come with a full bladder"
      }
    }
  ];

  // 3. Generate medical specialties
  const specialties: Specialty[] = [
    {
      id: "cardiology",
      name: {
        es: "Cardiología",
        en: "Cardiology"
      }
    },
    {
      id: "pathology",
      name: {
        es: "Medicina Interna",
        en: "Internal Medicine"
      }
    },
    {
      id: "radiology",
      name: {
        es: "Radiología",
        en: "Radiology"
      }
    },
    {
      id: "neurology",
      name: {
        es: "Neurología",
        en: "Neurology"
      }
    },
    {
      id: "orthopedics",
      name: {
        es: "Traumatología",
        en: "Orthopedics"
      }
    }
  ];

  // 4. Generate doctors
  const doctors: Doctor[] = [
    // Cardiologists
    {
      id: "doc-1",
      firstName: { es: "Antonio", en: "Antonio" },
      lastName: { es: "García Pérez", en: "Garcia Perez" },
      specialtyId: "cardiology",
      locationIds: ["hospital-1", "diagnostic-3"],
      languages: ["es", "en"],
      appointments: { duration: 30, daysAvailable: [1, 2, 3] } // Mon-Wed
    },
    {
      id: "doc-2",
      firstName: { es: "María", en: "Maria" },
      lastName: { es: "López Sánchez", en: "Lopez Sanchez" },
      specialtyId: "cardiology",
      locationIds: ["hospital-2", "diagnostic-2"],
      languages: ["es", "en", "fr"],
      appointments: { duration: 30, daysAvailable: [2, 3, 4, 5] } // Tue-Fri
    },
    
    // Internal Medicine
    {
      id: "doc-3",
      firstName: { es: "Carlos", en: "Carlos" },
      lastName: { es: "Martínez Ruiz", en: "Martinez Ruiz" },
      specialtyId: "pathology",
      locationIds: ["hospital-3", "diagnostic-1"],
      languages: ["es", "en"],
      appointments: { duration: 45, daysAvailable: [1, 3, 5] } // Mon, Wed, Fri
    },
    {
      id: "doc-4",
      firstName: { es: "Laura", en: "Laura" },
      lastName: { es: "Fernández Gómez", en: "Fernandez Gomez" },
      specialtyId: "pathology",
      locationIds: ["hospital-1", "diagnostic-4"],
      languages: ["es", "en", "de"],
      appointments: { duration: 45, daysAvailable: [1, 2, 4, 5] } // Mon, Tue, Thu, Fri
    },
    
    // Radiologists
    {
      id: "doc-5",
      firstName: { es: "Javier", en: "Javier" },
      lastName: { es: "Rodríguez Álvarez", en: "Rodriguez Alvarez" },
      specialtyId: "radiology",
      locationIds: ["hospital-1", "hospital-2"],
      languages: ["es", "en"],
      appointments: { duration: 30, daysAvailable: [1, 2, 3, 4, 5] } // Mon-Fri
    },
    
    // Neurologists
    {
      id: "doc-6",
      firstName: { es: "Elena", en: "Elena" },
      lastName: { es: "Navarro Torres", en: "Navarro Torres" },
      specialtyId: "neurology",
      locationIds: ["hospital-3", "diagnostic-2"],
      languages: ["es", "en", "it"],
      appointments: { duration: 60, daysAvailable: [2, 4] } // Tue, Thu
    },
    
    // Orthopedists
    {
      id: "doc-7",
      firstName: { es: "Miguel", en: "Miguel" },
      lastName: { es: "Moreno Ortega", en: "Moreno Ortega" },
      specialtyId: "orthopedics",
      locationIds: ["hospital-2", "diagnostic-1", "diagnostic-3"],
      languages: ["es", "en"],
      appointments: { duration: 40, daysAvailable: [1, 3, 5] } // Mon, Wed, Fri
    }
  ];

  // 5. Helper function to generate time slots
  function generateTimeSlots(): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1); // Mañana
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 61); // Mañana + 60 días
    let slotId = 1;
    
    // For each day in range
    for (let currentDate = new Date(startDate); currentDate <= endDate; currentDate.setDate(currentDate.getDate() + 1)) {
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ...
      const dateString = currentDate.toISOString().split('T')[0];
      
      // Opening hours: 8:00 AM to 8:00 PM
      const openingHours = [
        '08:00', '09:00', '10:00', '11:00',
        '12:00', '13:00', '14:00', '15:00',
        '16:00', '17:00', '18:00', '19:00'
      ];
      
      // No appointments on Sunday (dayOfWeek = 0)
      if (dayOfWeek === 0) continue;
      
      // 1. Generate slots for EXAMINATIONS (available at all locations)
      for (const location of locations) {
        for (const exam of examinations) {
          // Calculate how many slots can be scheduled based on examination duration
          const availableSlots = [];
          for (let i = 0; i < openingHours.length; i += exam.duration / 30) {
            if (i < openingHours.length) {
              availableSlots.push(openingHours[i]);
            }
          }
          
          for (const time of availableSlots) {
            // Randomly mark some slots as booked (20%)
            const isBooked = Math.random() < 0.2;
            
            slots.push({
              id: `slot-${slotId++}`,
              date: dateString,
              startTime: time,
              locationId: location.id,
              serviceType: 'examination',
              serviceId: exam.id,
              booked: isBooked
            });
          }
        }
      }
      
      // 2. Generate slots for DOCTORS (only on days they work)
      for (const doctor of doctors) {
        // Check if doctor works on this day of the week
        if (!doctor.appointments.daysAvailable.includes(dayOfWeek)) continue;
                
        // Create a simple hash based on date and doctor ID
        const dateHash = dateString.split('-').reduce((acc, curr) => acc + parseInt(curr, 10), 0);
        const doctorIdHash = parseInt(doctor.id.replace(/\D/g, '')) || doctor.id.charCodeAt(0);
        const combinedHash = (dateHash + doctorIdHash) % doctor.locationIds.length;
        
        const selectedLocationId = doctor.locationIds[combinedHash];
        
        // Calculate how many slots can be scheduled based on appointment duration
        const availableSlots = [];
        for (let i = 0; i < openingHours.length; i += doctor.appointments.duration / 30) {
          if (i < openingHours.length) {
            availableSlots.push(openingHours[i]);
          }
        }
        
        for (const time of availableSlots) {
          // Randomly mark some slots as booked (40%)
          const isBooked = Math.random() < 0.4;
          
          slots.push({
            id: `slot-${slotId++}`,
            date: dateString,
            startTime: time,
            locationId: selectedLocationId,
            serviceType: 'doctor',
            serviceId: doctor.id,
            booked: isBooked
          });
        }
      }
    }
    
    return slots;
  }
  
  // 6. Generate all time slots
  const slots = generateTimeSlots();
  
  // 7. Return all data sets, limiting the number of slots to prevent excessive data
  return {
    locations,
    examinations,
    specialties,
    doctors,
    slots: slots.slice(0, 40000) // Limit to not generate too much data
  };
}

// Helper function: add days to a date
export function getDatePlusDays(dateStr: string, days: number): string {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

// Helper function: find available slots
export function findAvailableSlots(
  data: HealthCareData, 
  params: AvailableSlotsParams
): TimeSlot[] {
  return data.slots.filter(slot => 
    (!slot.booked) &&
    (slot.serviceType === params.serviceType) &&
    (slot.serviceId === params.serviceId) &&
    (!params.date || slot.date === params.date) &&
    (!params.locationId || slot.locationId === params.locationId)
  );
}

// Helper function: find alternatives
export function findAlternatives(
  data: HealthCareData,
  params: AlternativesParams
): { 
  type: 'different-location' | 'different-doctor' | 'different-time' | 'different-day' | 'no-alternatives';
  slots: TimeSlot[];
} {
  // 1. Same time, different location
  const sameTimeOtherLocations = data.slots.filter(slot => 
    (!slot.booked) &&
    (slot.date === params.preferredDate) &&
    (slot.startTime === params.preferredTime) &&
    (slot.locationId !== params.preferredLocationId) &&
    (slot.serviceType === params.serviceType) &&
    (slot.serviceId === params.serviceId)
  );
  
  if (sameTimeOtherLocations.length > 0) {
    return {
      type: 'different-location',
      slots: sameTimeOtherLocations
    };
  }
  
  // 2. For doctor appointments, try a different doctor of the same specialty
  if (params.serviceType === 'doctor') {
    const doctor = data.doctors.find(d => d.id === params.serviceId);
    if (doctor) {
      const sameSpecialtyDoctors = data.doctors.filter(d => 
        (d.id !== params.serviceId) && 
        (d.specialtyId === doctor.specialtyId) &&
        d.locationIds.includes(params.preferredLocationId)
      );
      
      const alternativeDoctorSlots = data.slots.filter(slot => 
        (!slot.booked) &&
        (slot.date === params.preferredDate) &&
        (slot.startTime === params.preferredTime) &&
        (slot.locationId === params.preferredLocationId) &&
        (slot.serviceType === 'doctor') &&
        sameSpecialtyDoctors.some(d => d.id === slot.serviceId)
      );
      
      if (alternativeDoctorSlots.length > 0) {
        return {
          type: 'different-doctor',
          slots: alternativeDoctorSlots
        };
      }
    }
  }
  
  // 3. Same day, different time
  const sameLocationSameDay = data.slots.filter(slot => 
    (!slot.booked) &&
    (slot.date === params.preferredDate) &&
    (slot.locationId === params.preferredLocationId) &&
    (slot.serviceType === params.serviceType) &&
    (slot.serviceId === params.serviceId)
  ).sort((a, b) => a.startTime.localeCompare(b.startTime));
  
  if (sameLocationSameDay.length > 0) {
    return {
      type: 'different-time',
      slots: sameLocationSameDay
    };
  }
  
  // 4. Nearby days
  const nearbyDays = data.slots.filter(slot => 
    (!slot.booked) &&
    (slot.date > params.preferredDate) &&
    (slot.date <= getDatePlusDays(params.preferredDate, 7)) &&
    (slot.locationId === params.preferredLocationId) &&
    (slot.serviceType === params.serviceType) &&
    (slot.serviceId === params.serviceId)
  ).sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));
  
  if (nearbyDays.length > 0) {
    return {
      type: 'different-day',
      slots: nearbyDays
    };
  }
  
  return { type: 'no-alternatives', slots: [] };
}

/**
 * Determines which location a doctor will be at on a specific date
 * @param doctorId Doctor ID
 * @param dateStr Date in ISO YYYY-MM-DD format
 * @param doctors Array of doctors
 * @returns Location ID or null if the doctor is not available on that date
 */
export function getDoctorLocationForDate(
  doctorId: string,
  dateStr: string,
  doctors: Doctor[]
): string | null {
  // Find the doctor
  const doctor = doctors.find(d => d.id === doctorId);
  if (!doctor) return null;
  
  // Check if doctor works on this day of the week
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ...
  
  if (!doctor.appointments.daysAvailable.includes(dayOfWeek)) {
    return null; // Doctor doesn't work this day
  }
  
  // Use the same deterministic algorithm as in generateTimeSlots
  const dateHash = dateStr.split('-').reduce((acc, curr) => acc + parseInt(curr, 10), 0);
  const doctorIdHash = parseInt(doctor.id.replace(/\D/g, '')) || doctor.id.charCodeAt(0);
  const combinedHash = (dateHash + doctorIdHash) % doctor.locationIds.length;
  
  return doctor.locationIds[combinedHash];
}
